import type { PrimaryCompanyChange } from '../../../companies/index.js';
import {
  actorFromAuditMeta,
  diffRecords,
  ENTITY_HISTORY_ENTITY_TYPES,
  ENTITY_HISTORY_EVENT_TYPES,
  ENTITY_HISTORY_SOURCES,
  type EntityHistoryLatestScope,
  type EntityHistoryService,
  type EntityHistorySource,
  type ValueRecord,
} from '../../../entity-history/index.js';
import type { IamAuditContext } from '../../../permissions/index.js';
import {
  companyIdsOf,
  type UserAccessHistoryTarget,
} from './user-access-history.js';

/** The `users` columns a `USER_PROFILE` entry records, by their API names. */
export const USER_PROFILE_HISTORY_FIELDS = [
  'firstName',
  'lastName',
  'email',
  'langId',
  'developmentTag',
  'primaryCompanyId',
] as const;

export type UserProfileHistoryField =
  (typeof USER_PROFILE_HISTORY_FIELDS)[number];

/** A user's recorded fields as stored; a write passes only the ones it touches. */
export type UserProfileValues = Partial<
  Record<UserProfileHistoryField, string | null>
>;

/**
 * The entries whose actor is a user's "last updated by": the profile's own
 * entries, and an admin blocking or unblocking the account — the writes that
 * used to stamp the `users.updated_by_*` columns.
 */
export const USER_LAST_EDITOR_SCOPES: EntityHistoryLatestScope[] = [
  { entityType: ENTITY_HISTORY_ENTITY_TYPES.USER_PROFILE },
  { entityType: ENTITY_HISTORY_ENTITY_TYPES.USER_ACCESS, subTypes: ['BLOCK'] },
];

/** `users.email` is stored lower-cased, so the history records it that way. */
const normalise = (values: UserProfileValues): UserProfileValues =>
  values.email ? { ...values, email: values.email.toLowerCase() } : values;

const toValueRecord = (values: UserProfileValues): ValueRecord => {
  const normalised = normalise(values);
  return Object.fromEntries(
    USER_PROFILE_HISTORY_FIELDS.map(field => [
      field,
      { value: normalised[field] },
    ]),
  );
};

const baseEntry = (
  target: UserAccessHistoryTarget,
  source: EntityHistorySource,
) => ({
  entityType: ENTITY_HISTORY_ENTITY_TYPES.USER_PROFILE,
  entityId: target.userId,
  source,
  correlationId: target.correlationId ?? null,
  companyIds: companyIdsOf(target.primaryCompanyId),
  metadata: { entityType: ENTITY_HISTORY_ENTITY_TYPES.USER_PROFILE },
  createdBy: actorFromAuditMeta(target.audit),
});

/** A user was created — invited, or provisioned at sign-in — with these values. */
export const recordUserProfileCreatedHistory = async (
  history: EntityHistoryService,
  target: UserAccessHistoryTarget,
  values: UserProfileValues,
  source: EntityHistorySource,
): Promise<void> => {
  await history.record({
    ...baseEntry(target, source),
    eventType: ENTITY_HISTORY_EVENT_TYPES.CREATED,
    changes: diffRecords(undefined, toValueRecord(values), {
      fields: USER_PROFILE_HISTORY_FIELDS,
    }),
  });
};

/**
 * A user's fields were written. `previous` and `next` hold the same keys —
 * the fields the write could move — and nothing is recorded when none did.
 */
export const recordUserProfileUpdatedHistory = async (
  history: EntityHistoryService,
  target: UserAccessHistoryTarget,
  previous: UserProfileValues,
  next: UserProfileValues,
  source: EntityHistorySource,
): Promise<void> => {
  const changes = diffRecords(toValueRecord(previous), toValueRecord(next), {
    fields: USER_PROFILE_HISTORY_FIELDS,
  });
  if (changes.length === 0) return;

  await history.record({
    ...baseEntry(target, source),
    eventType: ENTITY_HISTORY_EVENT_TYPES.UPDATED,
    changes,
  });
};

/**
 * A user's primary company moved: their first company joined, the primary one
 * left (promoting the next, or leaving them companyless), or an admin chose
 * another. The entry belongs to the new primary company, or to Retraced's own
 * tenant for a user left companyless. Without `audit` the actor is unknown
 * and the source is `ADMIN`, as only admin routes move it.
 */
export const recordUserPrimaryCompanyChangeHistory = async (
  history: EntityHistoryService,
  change: PrimaryCompanyChange,
  audit: IamAuditContext | undefined,
): Promise<void> => {
  await recordUserProfileUpdatedHistory(
    history,
    {
      userId: change.userId,
      primaryCompanyId: change.to,
      audit: audit?.meta ?? {},
      correlationId: audit?.correlationId ?? null,
    },
    { primaryCompanyId: change.from },
    { primaryCompanyId: change.to },
    audit?.source ?? ENTITY_HISTORY_SOURCES.ADMIN,
  );
};
